import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Row,
  Col,
  Spin,
  Alert,
  Empty,
  Tag,
  Divider,
  message,
  Badge,
  Avatar,
  Tooltip,
  Descriptions,
} from "antd";
import {
  ShoppingCartOutlined,
  EyeOutlined,
  DollarOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  UserOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  StarFilled,
  ClockCircleOutlined,
  MedicineBoxOutlined,
  GiftOutlined,
  FireOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import { useDoctorQuery } from "../../hooks/useDoctorQuery";
import drEasyApi from "../../Api/Customapi";
import { getImg } from "../../Api/getImg";
import { useDoctorContext } from "../../context/DoctorContext";
import AnimatedButton from "../Buttons/AnimatedButton";
import { useLanguage } from "../../context/LanguageContext";
import { slug } from "../../utils/store";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const floatingVariants = {
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

const PackagePurchase = () => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const { data: doctor } = useDoctorQuery();
  const { setHasOffers } = useDoctorContext();
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();

  // Check if current path is "packages" to show all packages
  const url = window.location.href;
  const currentPath = url.substring(url.lastIndexOf("/") + 1);
  const isPackagesPage = currentPath === "packages";

  useEffect(() => {
    if (doctor && doctor.id) {
      loadPackagesByDoctor();
    } else {
    }
  }, [doctor, currentLanguage]);

  const loadPackagesByDoctor = async () => {
    if (!doctor?.id) {
      return;
    }

    setLoading(true);
    try {
      const url = `/api/Packages/GetByCenterId?CenterId=${doctor.id}`;

      const response = await drEasyApi.get(url);

   

      const normalizedPackages = (response.data || []).map((pkg) => {
        return {
          ...pkg,
          items: pkg.services || pkg.items || [],
        };
      });

      setPackages(normalizedPackages);
      if (normalizedPackages.length > 0) {
        setHasOffers(true);
      }
    } catch (err) {
    

      if (err.response) {
       
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {

    setPurchaseLoading(true);
    try {
      const purchaseData = {
        servicePackageId: packageId,
      };


      const response = await drEasyApi.post(
        "/api/Packages/Purchase",
        purchaseData,
      );


      message.success(t("packages.purchaseSuccess"));
      setIsPurchaseModalOpen(false);

      if (doctor?.id) {
        loadPackagesByDoctor();
      }
    } catch (error) {
      console.error("Error purchasing package:", error);
      console.error("Purchase error response:", error.response);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.title ||
        t("packages.purchaseFailed");

      message.error(errorMessage);
    } finally {
      setPurchaseLoading(false);
    }
  };

  const openPurchaseModal = (pkg) => {
    setSelectedPackage(pkg);
    setIsPurchaseModalOpen(true);
  };

  const closePurchaseModal = () => {
    setIsPurchaseModalOpen(false);
    setSelectedPackage(null);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedPackage(null);
  };

  const getPackageIcon = (index) => {
    const icons = [MedicineBoxOutlined, GiftOutlined, CrownOutlined];
    const IconComponent = icons[index % icons.length];
    return <IconComponent />;
  };

  const handleSeeMore = () => {
    navigate(`/packages`);
    navigate(`/packages`);
  };

  if (packages.length == 0) {
    setHasOffers(false);
    return null;
  }

  // Show packages based on current path
  const displayedPackages = isPackagesPage ? packages : packages.slice(0, 3);

  // Only show "See More" button if not on packages page and there are more than 3 packages
  const hasMorePackages = !isPackagesPage && packages.length > 3;

  return (
    <section className="min-h-screen  bg-cardColor overflow-hidden pb-10  mx-auto">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-32 h-32 bg-primary/5 rounded-full blur-xl"
          variants={floatingVariants}
          animate="animate"
        />
        <motion.div
          className="absolute bottom-32 right-16 w-48 h-48 bg-purple-300/10 rounded-full blur-2xl"
          variants={floatingVariants}
          animate="animate"
          style={{ animationDelay: "1s" }}
        />
        <motion.div
          className="absolute top-1/2 left-1/3 w-20 h-20 bg-blue-300/10 rounded-full blur-lg"
          variants={floatingVariants}
          animate="animate"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <div  className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 relative z-10 mb-5">
        <motion.div
          className="text-center mb-16 mx-auto flex flex-col items-center justify-center"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="relative mb-3">
            <div className="relative bg-goldy  p-3 rounded-full shadow-lg">
              <ShoppingCartOutlined className="text-2xl text-white" />
            </div>
          </div>

        <h2 className=" text-2xl md:text-4xl font-bold text-center goldy flex items-center justify-center gap-3">
            {isPackagesPage
              ? t("packages.allPremiumPackages")
              : t("packages.premiumPackages")}
          </h2>
        <p className="mt-3 text-textPrimary/70 text-sm md:text-base max-w-2xl mx-auto">
            {isPackagesPage
              ? t("packages.allPackagesDesc")
              : t("packages.premiumPackagesDesc")}
          </p>

          {/* Stats or highlights */}
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
              <StarFilled className="text-yellow-500" />
              <span className="text-sm font-medium text-gray-700">
                {t("packages.premiumQuality")}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
              <FireOutlined className="text-orange-500" />
              <span className="text-sm font-medium text-gray-700">
                {t("packages.bestValue")}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
              <CheckCircleOutlined className="text-green-500" />
              <span className="text-sm font-medium text-gray-700">
                {t("packages.trustedCare")}
              </span>
            </div>
          </div>
        </motion.div>

        {!doctor ? (
          <div className="text-center">
            <Spin size="large" />
            <p className="mt-4 text-lg text-gray-500">
              {t("packages.loadingDoctor")}
            </p>
          </div>
        ) : !doctor.id ? (
          <div className="text-center max-w-md mx-auto">
            <Alert
              message={t("packages.noDoctorId")}
              description={t("packages.noDoctorIdDesc")}
              type="warning"
              showIcon
              className="shadow-sm"
            />
          </div>
        ) : (
          <>
            {loading ? (
              <div className="text-center">
                <Spin size="large" />
                <p className="mt-4 text-lg text-gray-500">
                  {t("packages.loadingPackages")}
                </p>
              </div>
            ) : (
              <>
                <motion.div
                  className={`grid gap-4 md:gap-12 ${
                    isPackagesPage
                      ? "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 "
                      : "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3"
                  }`}
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                >
                  {displayedPackages.map((pkg, index) => {
                    return (
                      <motion.div
                        key={pkg.id}
                        className={`group relative rounded-lg md:rounded-2xl h-88 transition-all duration-500 hover:-translate-y-2 bg-white/80 backdrop-blur-sm border border-gray-200/50 shadow-lg hover:shadow-xl
                                                `}
                        variants={cardVariants}
                        whileHover={{ scale: 1.02 }}
                      >
                        <div className="p-4 md:p-8 text-center relative">
                          {/* Package Icon */}
                          <motion.div
                            className={`inline-flex items-center justify-center w-10 h-10 md:w-16 md:h-16 rounded-2xl mb-3 md:mb-6 
                                                           bg-gradient-to-r from-primary to-secondary text-white`}
                            whileHover={{ rotate: 360 }}
                            transition={{ duration: 0.6 }}
                          >
                            <span className="text-2xl">
                              {getPackageIcon(index)}
                            </span>
                          </motion.div>

                          {/* Package Title */}
                          <h3 className="text-lg md:text-2xl font-semibold text-primary mb-3 ">
                            {pkg.title}
                          </h3>
                          <hr></hr>
                          {/* Package Description */}
                          <p className="text-gray-600 mt-3 text-xs md:text-sm mb-6 line-clamp-3 leading-relaxed ">
                            {pkg.description ||
                              t("packages.comprehensivePackage")}
                          </p>

                          {/* Price Section */}
                          <div className="mb-6">
                            <div className="flex items-center justify-center gap-2 mb-2">
                              <span
                                className={`text-xl md:text-4xl font-bold text-primary flex`}
                              >
                                {Math.floor(pkg.price).toLocaleString(
                                  currentLanguage === "ar" ? "ar-EG" : "en-US",
                                )}{" "}
                                <p className="text-light text-lg md:text-2xl">
                                  {" "}
                                  {t("common.currency")}{" "}
                                </p>
                              </span>
                              {pkg.price % 1 !== 0 && (
                                <span className="text-lg text-gray-500 font-medium">
                                  {Math.round((pkg.price % 1) * 100)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center justify-center gap-1 text-gray-500 text-sm">
                              <ClockCircleOutlined />
                              <span>
                                {pkg.validityInDays
                                  ? `${pkg.validityInDays} ${t("packages.days")}`
                                  : t("packages.noLimits")}{" "}
                              </span>
                            </div>
                          </div>

                          {/* Services Preview */}
                          {pkg.items && pkg.items.length > 0 && (
                            <div className="mb-8">
                              <div className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">
                                {t("packages.includes")} {pkg.items.length}{" "}
                                {t("packages.services")}
                              </div>
                              <div className="flex flex-wrap justify-center gap-2">
                                {pkg.items.slice(0, 2).map((item, idx) => (
                                  <motion.span
                                    key={idx}
                                    className={`inline-flex items-center gap-1 px-1 md:px-3 py-1 md:py-1.5 rounded-full text-xs font-medium 
                                                                           bg-gray-100 text-gray-700
                                                                        `}
                                    whileHover={{ scale: 1.05 }}
                                  >
                                    <MedicineBoxOutlined className="text-xs" />
                                    {item.serviceName} ×{item.quantity}
                                  </motion.span>
                                ))}
                                {pkg.items.length > 2 && (
                                  <span
                                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700
                                                                    `}
                                  >
                                    +{pkg.items.length - 2} {t("packages.more")}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex gap-3 justify-center">
                            <Button
                            type=""
                              icon={<EyeOutlined />}
                              onClick={() => {
                                setSelectedPackage(pkg);
                                setIsDetailModalOpen(true);
                              }}
                              className="bg-goldy text-white transition-colors flex-1 h-10 md:h-11"
                            >
                              {t("packages.viewDetails")}
                            </Button>

                            {/* <Button
                                                        type=' '
                                                            className={`flex-1 h-11 flex text-white  justify-center items-center  font-semibold bg-primary hover:bg-primary/90
                                                            `}
                                                            secondary
                                                            onClick={() => openPurchaseModal(pkg)}
                                                        >
                                                            {t('packages.purchaseNow')}
                                                        </Button> */}
                          </div>
                        </div>

                        {/* Hover Glow Effect */}
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                      </motion.div>
                    );
                  })}
                </motion.div>

                {/* See More Button - only show if not on packages page and there are more packages */}
                {hasMorePackages && (
                  <div className="text-center mt-12">
                    <button
                      onClick={handleSeeMore}
                    >
                      {t("packages.seeMorePackages")}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <Modal
        title={
          <div className="flex items-center gap-3">
            <div className="  p-2 rounded-lg">
              <InfoCircleOutlined className="text-primary text-lg" />
            </div>
            <span className="text-xl font-bold">
              {t("packages.packageDetails")}
            </span>
          </div>
        }
        open={isDetailModalOpen}
        onCancel={closeDetailModal}
        footer={[
          <Button key="close" size="large" onClick={closeDetailModal}>
            {t("common.close")}
          </Button>,
          <Button
            key="purchase"
            type="primary"
            size="large"
            icon={<ShoppingCartOutlined />}
            onClick={() => {
              closeDetailModal();
              openPurchaseModal(selectedPackage);
            }}
            className="bg-primary border-none  shadow-lg px-3"
          >
            {t("packages.purchaseNow")}
          </Button>,
        ]}
        width={800}
        className="package-details-modal"
      >
        {selectedPackage && (
          <div className="py-4">
            {/* Package Header */}
            <div className="text-center mb-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary  rounded-2xl mb-4">
                <MedicineBoxOutlined className="text-2xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {selectedPackage.title}
              </h3>
              <div className="text-3xl font-bold text-primary mb-2 flex justify-center items-center gap-1">
                {selectedPackage.price}{" "}
                <span className="font-semibold text-secondary text-2xl">
                  {" "}
                  {t("common.currency")}
                </span>
              </div>
              <div className="text-gray-600">
                {t("packages.validFor")} {selectedPackage.validityInDays}{" "}
                {t("packages.days")}
              </div>
            </div>

            <Descriptions bordered column={2} size="middle" className="mb-6">
              <Descriptions.Item label={t("packages.description")} span={2}>
                <Text>
                  {selectedPackage.description ||
                    t("packages.comprehensivePackage")}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            {selectedPackage.items && selectedPackage.items.length > 0 && (
              <>
                <Divider>
                  <span className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                    <MedicineBoxOutlined />
                    {t("packages.includedServices")} (
                    {selectedPackage.items.length})
                  </span>
                </Divider>
                <div className="max-h-64 overflow-y-auto space-y-3">
                  {selectedPackage.items.map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex justify-between items-center p-4 bg-gradient-to-r from-blue-50/50 to-purple-50/30 rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <MedicineBoxOutlined className="text-primary" />
                        </div>
                        <Text strong className="text-gray-800">
                          {item.serviceName}
                        </Text>
                      </div>
                      <Badge
                        count={`×${item.quantity}`}
                        style={{
                          backgroundColor: "gray",
                          fontSize: "14px",
                          height: "24px",
                          lineHeight: "22px",
                          minWidth: "32px",
                        }}
                      />
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={
          <div className="flex items-center gap-3">
            <div className=" p-2 rounded-lg">
              <ShoppingCartOutlined className="text-primary text-lg" />
            </div>
            <span className="text-xl font-bold">
              {t("packages.confirmPurchase")}
            </span>
          </div>
        }
        open={isPurchaseModalOpen}
        onCancel={closePurchaseModal}
        footer={[
          <Button key="cancel" size="large" onClick={closePurchaseModal}>
            {t("common.cancel")}
          </Button>,
          // <Button
          //     key="confirm"
          //     type="primary"
          //     size="large"
          //     loading={purchaseLoading}
          //     icon={<CheckCircleOutlined />}
          //     onClick={() => handlePurchase(selectedPackage?.id)}
          //     className="bg-primary border-none hover:from-green-600 hover:to-emerald-700 shadow-lg px-8"
          // >
          //     {t('packages.confirmPurchase')}
          // </Button>
        ]}
        width={600}
      >
        {selectedPackage && (
          <div className="py-4">
            <div className="bg-primary/10 to-emerald-50 p-6 rounded-xl border border-green-200 mb-6">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary to-emerald-600 rounded-xl mb-3">
                  <MedicineBoxOutlined className="text-white text-lg" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  {selectedPackage.title}
                </h3>
              </div>

              <Descriptions column={1} size="middle" bordered={false}>
                <Descriptions.Item label={t("packages.totalPrice")}>
                  <Text strong className="text-primary text-2xl">
                    {t("common.currency")} {selectedPackage.price}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label={t("packages.validityPeriod")}>
                  <div className="flex items-center gap-2">
                    <CalendarOutlined className="text-blue-500" />
                    <span>
                      {selectedPackage.validityInDays}{" "}
                      {t("packages.daysFromPurchase")}
                    </span>
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label={t("packages.servicesIncluded")}>
                  <div className="flex items-center gap-2">
                    <MedicineBoxOutlined className="text-purple-500" />
                    <span>
                      {selectedPackage.items?.length || 0}{" "}
                      {t("packages.healthcareServices")}
                    </span>
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
};

export default PackagePurchase;
